import React, { useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../helpers";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { LoadingState, DownloadAsExcelSheet } from "../../../components";
import { FaWhatsapp, FaPhoneAlt, FaClipboardList } from "react-icons/fa";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import VoterReachOutSectionHeader from "../voter-reachout-section-header";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

type TransactionFilters = {
    type: string;
    status: string;
    dateRange: string;
};

const initialFilters: TransactionFilters = {
    type: "",
    status: "",
    dateRange: "",
};

export const VoterReachOutTransactions = observer(() => {
    const { voterReachOutStore } = useStore();
    const { projectid } = useParams<{ projectid: string }>();
    const gridRef = useRef<any>(null);

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<TransactionFilters>(initialFilters);

    // Fetch transactions
    const {
        data: transactionData = [],
        isLoading: isTransactionsLoading,
        isError: isTransactionsError,
        error: transactionsError,
    } = useQuery({
        queryKey: ["voterReachOutTransactions", projectid],
        queryFn: async () => {
            if (!projectid) {
                throw new Error("Project ID is required");
            }

            const res = await voterReachOutStore.get_transactions({
                projectid,
            });

            if (res?.status === 200) {
                return res?.data || [];
            }

            throw new Error("Failed to fetch transactions");
        },
        enabled: Boolean(projectid),
        retry: 2,
    });

    // Fetch credit balance
    const {
        data: creditData,
        isLoading: isCreditLoading,
        isError: isCreditError,
    } = useQuery({
        queryKey: ["voterReachOutCredits", projectid],
        queryFn: async () => {
            if (!projectid) {
                throw new Error("Project ID is required");
            }

            const res = await voterReachOutStore.get_credit_balance({
                projectid,
            });

            if (res?.status === 200) {
                return res?.data || {};
            }

            throw new Error("Failed to fetch credit balance");
        },
        enabled: Boolean(projectid),
        retry: 2,
    });

    // Credit summary values
    const whatsappCredits = Number(creditData?.wa_bal ?? 0);
    const voiceCredits = Number(creditData?.voice_bal ?? 0);
    const totalTransactions = transactionData.length;
    const summaryCards = [
        {
            label: "WhatsApp Credits",
            value: whatsappCredits,
            description: "Available balance",
            icon: FaWhatsapp,
            iconStyle: "bg-emerald-50 text-emerald-600",
            valueStyle: "text-emerald-600",
            borderStyle: "border-emerald-100",
            decorationStyle: "bg-emerald-50",
        },
        {
            label: "Voice Call Credits",
            value: voiceCredits,
            description: "Available balance",
            icon: FaPhoneAlt,
            iconStyle: "bg-blue-50 text-blue-600",
            valueStyle: "text-blue-600",
            borderStyle: "border-blue-100",
            decorationStyle: "bg-blue-50",
        },
        {
            label: "Total Transactions",
            value: totalTransactions,
            description: "All recorded transactions",
            icon: FaClipboardList,
            iconStyle: "bg-indigo-50 text-indigo-600",
            valueStyle: "text-indigo-600",
            borderStyle: "border-indigo-100",
            decorationStyle: "bg-indigo-50",
        },
    ];

    // Dropdown options
    const filterOptions = useMemo(
        () => ({
            type: [
                ...new Set(
                    transactionData.map((item: any) => String(item.type ?? "").trim()).filter(Boolean)
                ),
            ] as string[],
            status: [
                ...new Set(
                    transactionData.map((item: any) => String(item.status ?? "").trim()).filter(Boolean)
                ),
            ] as string[],
        }),
        [transactionData]
    );

    // Filter transactions
    const filteredTransactions = useMemo(() => {
        const now = dayjs();
        const sevenDaysAgo = now.subtract(7, "day");
        const thirtyDaysAgo = now.subtract(30, "day");
        const query = search.trim().toLowerCase();

        return transactionData.filter((item: any) => {
            const type = String(item.type ?? "");
            const status = String(item.status ?? "");
            const campaign = String(item.campaign ?? "");
            const created = item.created ? dayjs(item.created) : null;

            const searchMatch = !query || campaign.toLowerCase().includes(query);

            const typeMatch = !filters.type || type.toLowerCase() === filters.type.toLowerCase();

            const statusMatch = !filters.status || status.toLowerCase() === filters.status.toLowerCase();

            let dateMatch = true;

            if (filters.dateRange) {
                if (!created || !created.isValid()) {
                    dateMatch = false;
                } else if (filters.dateRange === "today") {
                    dateMatch = created.isSame(now, "day");
                } else if (filters.dateRange === "7days") {
                    dateMatch = !created.isBefore(sevenDaysAgo) && !created.isAfter(now);
                } else if (filters.dateRange === "30days") {
                    dateMatch = !created.isBefore(thirtyDaysAgo) && !created.isAfter(now);
                }
            }

            return searchMatch && typeMatch && statusMatch && dateMatch;
        });
    }, [transactionData, search, filters]);

    // Export columns
    const exportCols = useMemo(
        () => [
            { label: "Type", key: "type" },
            { label: "Credits", key: "credits" },
            { label: "Status", key: "status" },
            { label: "Campaign Name", key: "campaign" },
            { label: "Date & Time", key: "createdFormatted" },
        ],
        []
    );

    // Export currently filtered transactions
    const exportData = useMemo(
        () =>
            filteredTransactions.map((item: any) => ({
                ...item,
                createdFormatted: item.created ? dayjs(item.created).format("DD MMM YYYY, hh:mm A") : "—",
            })),
        [filteredTransactions]
    );

    // AG Grid columns
    const columnDefs = useMemo(
        () => [
            {
                headerName: "#",
                valueGetter: (params: any) => (params.node?.rowIndex ?? 0) + 1,
                width: 75,
                minWidth: 65,
                sortable: true,
                pinned: "left" as const,
                cellStyle: {
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#64748b",
                },
            },
            {
                headerName: "Type",
                field: "type",
                minWidth: 165,
                flex: 1,
                sortable: true,
                cellRenderer: (params: any) => {
                    const type = String(params.value ?? "").toLowerCase();

                    if (type.includes("whatsapp")) {
                        return (
                            <div className="flex h-full items-center">
                                <span className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                                    <FaWhatsapp className="text-base" />
                                    WhatsApp
                                </span>
                            </div>
                        );
                    }

                    if (type.includes("voice")) {
                        return (
                            <div className="flex h-full items-center">
                                <span className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                                    <FaPhoneAlt className="text-sm" />
                                    Voice Call
                                </span>
                            </div>
                        );
                    }

                    return (
                        <div className="flex h-full items-center">
                            <span>{params.value || "—"}</span>
                        </div>
                    );
                },
            },
            {
                headerName: "Credits",
                field: "credits",
                minWidth: 110,
                flex: 0.8,
                sortable: true,
                cellStyle: {
                    fontWeight: 700,
                    color: "#334155",
                    textAlign: "right",
                },
                valueFormatter: (params: any) => Number(params.value ?? 0).toLocaleString(),
            },
            {
                headerName: "Status",
                field: "status",
                minWidth: 130,
                flex: 0.9,
                sortable: true,
                cellRenderer: (params: any) => {
                    const status = String(params.value ?? "");
                    const isDebited = status.toLowerCase() === "debited";

                    return (
                        <div className="flex h-full items-center">
                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                    isDebited ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
                                }`}
                            >
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                        isDebited ? "bg-rose-500" : "bg-emerald-500"
                                    }`}
                                />
                                {status || "—"}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: "Campaign Name",
                field: "campaign",
                minWidth: 200,
                flex: 1.5,
                sortable: true,
                cellRenderer: (params: any) => (
                    <div className="flex h-full items-center">
                        <span className="truncate font-medium text-slate-800" title={params.value || ""}>
                            {params.value || "—"}
                        </span>
                    </div>
                ),
            },
            {
                headerName: "Date & Time",
                field: "created",
                minWidth: 190,
                flex: 1.3,
                sortable: true,
                valueFormatter: (params: any) =>
                    params.value ? dayjs(params.value).format("DD MMM YYYY, hh:mm A") : "—",
                cellStyle: {
                    color: "#64748b",
                    fontSize: "12px",
                },
            },
        ],
        []
    );

    // Common grid settings
    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            sortable: true,
            filter: false,
        }),
        []
    );

    if (isTransactionsLoading || isCreditLoading) {
        return (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <LoadingState />
            </div>
        );
    }

    if (isTransactionsError || isCreditError) {
        return (
            <div className="mt-4 rounded-2xl border border-rose-100 bg-white p-8 shadow-sm">
                <div className="text-center">
                    <div className="mb-4 text-4xl text-rose-500">⚠️</div>
                    <h3 className="mb-2 text-lg font-semibold text-slate-900">Failed to Load Transactions</h3>
                    <p className="text-sm text-slate-600">Please try again later.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4 space-y-4">
            <div className="w-full space-y-6">
                <VoterReachOutSectionHeader
                    title="Transaction History"
                    description="Monitor your campaign credits and transaction activity."
                    icon={BanknotesIcon}
                    action={
                        <DownloadAsExcelSheet
                            variant="button"
                            name="Voter_Reach_Out_Transactions"
                            data={exportData}
                            cols={exportCols}
                            buttonLabel="Export to Excel"
                            buttonProps={{
                                variant: "contained",
                                color: "success",
                                disabled: exportData.length === 0,
                                sx: {
                                    borderRadius: "10px",
                                    padding: "9px 16px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    boxShadow: "none",
                                    "&:hover": {
                                        boxShadow: "none",
                                    },
                                },
                            }}
                        />
                    }
                />

                {/* Summary Cards */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {summaryCards.map((card) => {
                        const Icon = card.icon;

                        return (
                            <div
                                key={card.label}
                                className={`group relative overflow-hidden rounded-2xl border ${card.borderStyle} bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md`}
                            >
                                <div
                                    className={`absolute right-0 top-0 h-24 w-24 -translate-y-7 translate-x-7 rounded-full ${card.decorationStyle} transition-transform duration-200 group-hover:scale-125`}
                                />
                                <div className="relative flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-500">{card.label}</p>
                                        <p
                                            className={`mt-3 text-3xl font-bold tracking-tight ${card.valueStyle}`}
                                        >
                                            {card.value.toLocaleString()}
                                        </p>
                                        <p className="mt-1 text-xs text-slate-400">{card.description}</p>
                                    </div>
                                    <div
                                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.iconStyle}`}
                                    >
                                        <Icon className="text-lg" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Search and Filters */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-base font-bold text-slate-900">Filter Transactions</h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Narrow down transactions by campaign, type, status, or date.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setSearch("");
                                setFilters(initialFilters);
                            }}
                            disabled={!search && !Object.values(filters).some(Boolean)}
                            className="self-start rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 sm:self-auto"
                        >
                            Reset filters
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        {/* Campaign Search */}
                        <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Campaign
                            </label>
                            <input
                                type="text"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search campaign..."
                                aria-label="Search campaign"
                                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                            />
                        </div>

                        {/* Type Filter */}
                        <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Transaction Type
                            </label>
                            <select
                                value={filters.type}
                                onChange={(event) =>
                                    setFilters((previous) => ({
                                        ...previous,
                                        type: event.target.value,
                                    }))
                                }
                                aria-label="Filter by type"
                                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                            >
                                <option value="">All Types</option>
                                {filterOptions.type.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Transaction Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(event) =>
                                    setFilters((previous) => ({
                                        ...previous,
                                        status: event.target.value,
                                    }))
                                }
                                aria-label="Filter by status"
                                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                            >
                                <option value="">All Statuses</option>
                                {filterOptions.status.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range Filter */}
                        <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Date Range
                            </label>
                            <select
                                value={filters.dateRange}
                                onChange={(event) =>
                                    setFilters((previous) => ({
                                        ...previous,
                                        dateRange: event.target.value,
                                    }))
                                }
                                aria-label="Filter by date range"
                                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                            >
                                <option value="">All Dates</option>
                                <option value="today">Today</option>
                                <option value="7days">Last 7 Days</option>
                                <option value="30days">Last 30 Days</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Transaction Table */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-base font-bold text-slate-900">All Transactions</h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Review your credit usage and campaign activity.
                            </p>
                        </div>

                        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-indigo-50 px-3.5 py-2 text-sm font-semibold text-indigo-700">
                            <span className="h-2 w-2 rounded-full bg-indigo-500" />
                            {filteredTransactions.length} of {totalTransactions} records
                        </div>
                    </div>

                    {filteredTransactions.length === 0 ? (
                        <div className="flex min-h-[280px] flex-col items-center justify-center px-5 text-center">
                            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-3xl">
                                📭
                            </div>
                            <h3 className="font-semibold text-slate-800">No transactions found</h3>
                            <p className="mt-2 max-w-sm text-sm text-slate-500">
                                Try another campaign name or reset your filters to see all transactions.
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch("");
                                    setFilters(initialFilters);
                                }}
                                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <div
                            className="ag-theme-quartz w-full"
                            style={
                                {
                                    height: 600,
                                    "--ag-font-family": "inherit",
                                    "--ag-font-size": "13px",
                                    "--ag-border-color": "#E2E8F0",
                                    "--ag-header-background-color": "#F8FAFC",
                                    "--ag-header-foreground-color": "#475569",
                                    "--ag-odd-row-background-color": "#FAFBFF",
                                    "--ag-row-hover-color": "#F1F5FF",
                                    "--ag-row-border-color": "#F1F5F9",
                                    "--ag-header-height": "52px",
                                    "--ag-row-height": "54px",
                                } as React.CSSProperties
                            }
                        >
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredTransactions}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                animateRows={true}
                                suppressCellFocus={true}
                                pagination={true}
                                paginationPageSize={20}
                                headerHeight={52}
                                rowHeight={54}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
